/**
 * Console-inspired premium tokens — Tailwind class fragments.
 * Use for the LockedIn entertainment shell; do not hardcode one-off styles.
 */

export const consoleColors = Object.freeze({
  bg: {
    primary: 'bg-[#07090d]',
    secondary: 'bg-[#0d1118]',
    canvas: 'console-app-bg',
  },
  surface: {
    solid: 'bg-[#141923]',
    elevated: 'bg-[rgba(31,38,50,0.92)]',
    soft: 'bg-[rgba(20,25,35,0.82)]',
    hero: 'bg-[rgba(20,25,35,0.65)]',
  },
  text: {
    primary: 'text-[#f7f9fc]',
    secondary: 'text-[#9da7b6]',
    muted: 'text-[#687386]',
  },
  accent: {
    primary: 'text-[#8cff4a]',
    primaryBg: 'bg-[#8cff4a]',
    primaryBgHover: 'hover:bg-[#9dff5c]',
    secondary: 'text-[#33d6ff]',
    secondaryBg: 'bg-[#33d6ff]',
    live: 'text-[#ff6b2c]',
    liveBg: 'bg-[#ff6b2c]',
    danger: 'text-[#ff4f67]',
    warning: 'text-[#ffc857]',
    onPrimary: 'text-[#07090d]',
  },
  border: {
    soft: 'border-white/[0.08]',
    active: 'border-[rgba(140,255,74,0.45)]',
    live: 'border-[rgba(255,107,44,0.5)]',
  },
});

export const consoleRadii = Object.freeze({
  sm: 'rounded-[10px]',
  md: 'rounded-2xl',
  lg: 'rounded-3xl',
  pill: 'rounded-full',
});

export const consoleShadows = Object.freeze({
  card: 'shadow-[0_18px_60px_rgba(0,0,0,0.32)]',
  glow: 'shadow-[0_0_32px_rgba(140,255,74,0.18)]',
  glowLive: 'shadow-[0_0_28px_rgba(255,107,44,0.22)]',
  lift: 'shadow-[0_24px_48px_rgba(0,0,0,0.45)]',
});

export const consoleMotion = Object.freeze({
  fast: 'duration-[160ms] ease-out',
  normal: 'duration-[260ms] ease-out',
  lift: 'transition-all duration-[260ms] ease-out motion-safe:hover:-translate-y-1 motion-safe:hover:shadow-[0_24px_48px_rgba(0,0,0,0.45)]',
  press: 'active:scale-[0.97] transition-transform duration-[160ms]',
  focus: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8cff4a]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#07090d]',
});

export const consoleTypography = Object.freeze({
  hero: 'console-hero-title text-[#f7f9fc]',
  section: 'console-section-title text-[#f7f9fc]',
  card: 'console-card-title text-[#f7f9fc]',
  meta: 'console-meta-text',
  railLabel: 'text-[11px] font-bold uppercase tracking-[0.14em] text-[#9da7b6]',
  eyebrow: 'text-[10px] font-black uppercase tracking-[0.22em]',
});

/** Three surface tiers — solid, hero overlay, elevated interactive */
export const consoleSurfaces = Object.freeze({
  solid: `${consoleColors.surface.solid} ${consoleColors.border.soft} border ${consoleRadii.md} ${consoleShadows.card}`,
  elevated: `${consoleColors.surface.elevated} ${consoleColors.border.soft} border ${consoleRadii.md} ${consoleShadows.card} console-lift-hover ${consoleMotion.lift}`,
  hero: `${consoleColors.surface.hero} ${consoleColors.border.soft} border ${consoleRadii.lg} backdrop-blur-md`,
});

/** Modal / sheet chrome — shared across Lockbox overlays */
export const consoleModal = Object.freeze({
  scrim: 'bg-[#07090d]/80 backdrop-blur-md',
  panel: `bg-[#141923] border border-white/[0.08] ${consoleRadii.lg} ${consoleShadows.lift}`,
  panelMobile: 'max-md:rounded-t-3xl max-md:rounded-b-none',
  headerBorder: 'border-white/[0.08]',
  footerBorder: 'border-white/[0.08]',
  accent: {
    brand: 'border-[rgba(140,255,74,0.35)]',
    brandStrong: 'border-2 border-[rgba(140,255,74,0.45)]',
    live: 'border-[rgba(255,107,44,0.45)]',
    wallet: 'border-[rgba(51,214,255,0.35)]',
    friends: 'border-[rgba(140,255,74,0.28)]',
    danger: 'border-[rgba(255,79,103,0.4)]',
    dangerStrong: 'border-2 border-[rgba(255,79,103,0.45)]',
    warning: 'border-2 border-[rgba(255,200,87,0.4)]',
    neutral: 'border-white/[0.08]',
  },
});
